import React, { useState } from 'react';
import {
  AlertTriangle,
  Target,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  Zap,
  Shield,
  Brain,
  TrendingUp,
  Users,
  MessageSquare,
  Heart,
  Calendar,
} from 'lucide-react';

import { T, card, sectionLabel, heading, iconBox } from '../utils/adminDesignTokens';

const TABS = ['Product POV', 'North Star', 'Data to Collect', 'Customer Journey', 'Experiments', 'Roadmap OS'] as const;
type TabName = typeof TABS[number];

const EXPERIMENT_STATUSES = {
  'Not Started': { bg: T.limestone, text: T.textSecondary, icon: Clock },
  'Running': { bg: T.cedarSubtle, text: T.cedarPrimary, icon: Play },
  'Complete': { bg: '#EFF6FF', text: '#2563EB', icon: CheckCircle },
  'Killed': { bg: '#FEF2F2', text: '#DC2626', icon: XCircle },
} as const;
type ExperimentStatus = keyof typeof EXPERIMENT_STATUSES;

interface Experiment { name: string; hypothesis: string; successMetric: string; status: ExperimentStatus; outcome: string; priority: number; }

const EXPERIMENTS: Experiment[] = [
  { name: 'Day-3 Check-in', hypothesis: 'Proactive outreach on Day 3 reduces early churn by reminding users Cedar is paying attention before they ghost.', successMetric: 'Day-7 retention rate increases by 10%+ vs. control', status: 'Not Started', outcome: '', priority: 1 },
  { name: 'Missed Session Recovery', hypothesis: 'Reaching out when a scheduled session is skipped shows Cedar cares about consistency, not just content delivery.', successMetric: 'Session completion in 48h after miss increases by 15%+ vs. no outreach', status: 'Not Started', outcome: '', priority: 2 },
  { name: 'Memory Surface at Week 2', hypothesis: 'Showing users what Cedar has learned about them creates an aha moment that increases retention.', successMetric: 'Week-4 retention increases by 12%+ vs. control', status: 'Not Started', outcome: '', priority: 3 },
  { name: 'Recalibration After Goal Miss', hypothesis: 'Prompting recalibration after a missed weekly goal shows adaptability and prevents frustration churn.', successMetric: '20%+ higher next-week session completion', status: 'Not Started', outcome: '', priority: 4 },
  { name: 'Weekly Progress Summary', hypothesis: 'Weekly summary showing progress + memory capital growth reinforces value of sticking with Cedar.', successMetric: 'Month-2 retention increases by 8%+', status: 'Not Started', outcome: '', priority: 5 },
];

/* Styles imported from adminDesignTokens.ts */

export const AdminProductManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabName>('Product POV');
  const [expandedExperiment, setExpandedExperiment] = useState<string | null>(null);

  return (
    <div style={{ fontFamily: T.fontBody }} className="space-y-4">
      {/* Page Header */}
      <div>
        <h1 style={{ ...heading, fontSize: '24px' }}>Product Management</h1>
        <p style={{ fontSize: '13px', color: T.textSecondary, marginTop: '2px' }}>Strategy, roadmap, and experiments</p>
      </div>

      {/* 90% stat card */}
      <div style={{ ...card, background: `linear-gradient(135deg, ${T.cedarInk} 0%, ${T.cedarDark} 100%)`, padding: '16px 20px' }}>
        <div className="flex items-center gap-3">
          <div style={iconBox(`${T.cedarStream}20`)}>
            <AlertTriangle style={{ width: '16px', height: '16px', color: T.cedarStream }} />
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span style={{ ...heading, fontSize: '24px', color: '#FFF' }}>90%</span>
              <span style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>fitness app abandonment rate</span>
            </div>
            <p style={{ fontSize: '12px', color: T.cedarMist, marginTop: '4px', lineHeight: 1.4 }}>
              Coach Cedar's product strategy is the antidote — compounding memory capital, not disposable content.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1" style={{ padding: '3px', backgroundColor: T.limestone, borderRadius: T.radiusMd }}>
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="flex-1"
            style={{
              padding: '8px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '7px', border: 'none', cursor: 'pointer', fontFamily: T.fontDisplay, transition: 'all 0.15s',
              ...(activeTab === tab
                ? { backgroundColor: T.cedarPrimary, color: '#FFF', boxShadow: '0 1px 3px rgba(31,108,79,0.25)' }
                : { backgroundColor: 'transparent', color: T.textSecondary }),
            }}
            onMouseEnter={(e) => { if (activeTab !== tab) { e.currentTarget.style.backgroundColor = T.cedarSubtle; e.currentTarget.style.color = T.cedarPrimary; } }}
            onMouseLeave={(e) => { if (activeTab !== tab) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = T.textSecondary; } }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'Product POV' && <ProductPOVTab />}
      {activeTab === 'North Star' && <NorthStarTab />}
      {activeTab === 'Data to Collect' && <DataToCollectTab />}
      {activeTab === 'Customer Journey' && <CustomerJourneyTab />}
      {activeTab === 'Experiments' && <ExperimentsTab expanded={expandedExperiment} setExpanded={setExpandedExperiment} />}
      {activeTab === 'Roadmap OS' && <RoadmapOSTab />}
    </div>
  );
};

/* ─── Tab 1: Product POV ─── */
const ProductPOVTab: React.FC = () => (
  <div className="space-y-4">
    {/* Governing Belief — compact */}
    <div style={card}>
      <p style={sectionLabel}>The Governing Belief</p>
      <p style={{ paddingLeft: '12px', borderLeft: `3px solid ${T.cedarStream}`, fontStyle: 'italic', color: '#374151', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
        Every other fitness app sells content. We sell commitment. The product is not the workout plan. The product is the coach who knows you — your history, your excuses, your wins, your why. Every feature either deepens that relationship or it ships without us.
      </p>
    </div>

    {/* Principles — 2-col grid to reduce scroll */}
    <p style={sectionLabel}>Product Principles</p>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {[
        { title: 'Memory Capital is the moat', body: 'Every session, log, and recalibration makes Cedar harder to leave. Does this deepen what Cedar knows about the user?', icon: Brain },
        { title: 'Speed removes friction, context removes doubt', body: 'Ship fast, but every sprint must serve the bond, not just close tickets.', icon: Zap },
        { title: 'Behavior change, not content consumption', body: 'Completed sessions that create enacted change are the only vanity-free metric. Did the user do something?', icon: Target },
        { title: 'Proactive > reactive', body: 'Cedar should reach out before the user ghosts. Cedar-initiated re-engagement is worth 10x user-initiated.', icon: MessageSquare },
        { title: 'Build the bond, kill the bloat', body: "Any feature that doesn't reduce churn, increase completion, or deepen memory capital is deprioritized.", icon: Shield },
      ].map((p) => {
        const Icon = p.icon;
        return (
          <div key={p.title} style={card} className="flex gap-3">
            <div style={iconBox(T.cedarSubtle)}><Icon style={{ width: '16px', height: '16px', color: T.cedarPrimary }} /></div>
            <div>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: T.cedarInk }}>{p.title}</h3>
              <p style={{ fontSize: '12px', color: T.textSecondary, marginTop: '2px', lineHeight: 1.4 }}>{p.body}</p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ─── Tab 2: North Star — all in one view ─── */
const NorthStarTab: React.FC = () => (
  <div className="space-y-4">
    {/* Core metrics + vanity flags + memory capital in condensed layout */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {/* Session Completion */}
      <div style={card}>
        <div className="flex items-center gap-2 mb-2">
          <div style={iconBox(T.cedarSubtle)}><CheckCircle style={{ width: '16px', height: '16px', color: T.cedarPrimary }} /></div>
          <h3 style={{ ...heading, fontSize: '14px' }}>Session Completion Rate</h3>
        </div>
        <p style={{ fontSize: '12px', color: T.textSecondary, lineHeight: 1.4 }}>Did the user do the thing? The most honest signal of real behavior change.</p>
      </div>
      {/* Recalibration */}
      <div style={card}>
        <div className="flex items-center gap-2 mb-2">
          <div style={iconBox(`${T.cedarStream}15`)}><TrendingUp style={{ width: '16px', height: '16px', color: T.cedarStream }} /></div>
          <h3 style={{ ...heading, fontSize: '14px' }}>Recalibration Rate</h3>
        </div>
        <p style={{ fontSize: '12px', color: T.textSecondary, lineHeight: 1.4 }}>Is Cedar adapting to real life? Recalibrations prove the coach is listening.</p>
      </div>
    </div>

    {/* Vanity flags + Memory Capital side by side */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {/* Vanity flags */}
      <div style={card}>
        <p style={sectionLabel}>Vanity Metric Flags</p>
        <div className="space-y-2">
          {[
            { t: '"Plans created" is vanity', b: 'Unless paired with adherence rate.' },
            { t: '"Chat messages" is gameable', b: 'Unless quality-gated by intent.' },
          ].map((f) => (
            <div key={f.t} className="flex items-start gap-2" style={{ padding: '8px 10px', borderRadius: T.radiusSm, background: '#FFF7ED' }}>
              <AlertTriangle style={{ width: '14px', height: '14px', color: '#EA580C', flexShrink: 0, marginTop: '1px' }} />
              <div>
                <p style={{ fontSize: '12px', fontWeight: 600, color: '#9A3412' }}>{f.t}</p>
                <p style={{ fontSize: '11px', color: '#C2410C' }}>{f.b}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Memory Capital */}
      <div style={card}>
        <div className="flex items-center gap-2 mb-2">
          <div style={iconBox(T.cedarSubtle)}><Brain style={{ width: '16px', height: '16px', color: T.cedarPrimary }} /></div>
          <h3 style={{ ...heading, fontSize: '14px' }}>Memory Capital Score</h3>
        </div>
        <p style={{ fontSize: '11px', color: T.textSecondary, marginBottom: '8px' }}>Proprietary retention predictor — no competitor can replicate.</p>
        <div className="grid grid-cols-4 gap-2">
          {['Recals', 'Goals', 'Check-ins', 'Tenure'].map((m) => (
            <div key={m} style={{ textAlign: 'center', padding: '8px 4px', borderRadius: T.radiusSm, backgroundColor: T.limestone }}>
              <p style={{ fontSize: '10px', color: T.textTertiary }}>{m}</p>
              <p style={{ fontSize: '16px', fontWeight: 700, color: T.cedarPrimary }}>--</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ─── Tab 3: Data to Collect ─── */
const DataToCollectTab: React.FC = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {[
        { tag: 'P0', color: T.cedarPrimary, title: 'Session Drop-off Point', body: 'Where in the flow do users abandon?', detail: 'Onboarding steps, workout start/pause/complete, chat depth' },
        { tag: 'P0', color: T.cedarStream, title: 'Memory Capital Score', body: 'Composite metric per user cohort.', detail: 'Recalibrations, goals, check-ins, tenure, regularity' },
      ].map((c) => (
        <div key={c.title} style={card}>
          <div className="flex items-center gap-2 mb-2">
            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '9999px', color: '#FFF', backgroundColor: c.color }}>{c.tag}</span>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: T.cedarInk }}>{c.title}</h3>
          </div>
          <p style={{ fontSize: '12px', color: T.textSecondary }}>{c.body}</p>
          <p style={{ fontSize: '11px', color: T.textTertiary, marginTop: '6px' }}>{c.detail}</p>
        </div>
      ))}
    </div>

    <div style={card}>
      <p style={sectionLabel}>Framework</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { cat: 'Behavioral', items: ['Session timestamps', 'Pause/resume events', 'Intent classification', 'Plan follow-through', 'Recalibration triggers'] },
          { cat: 'Engagement', items: ['Time-to-first-session', 'Session regularity', 'Debrief completion', 'Check-in response', 'Goal modifications'] },
          { cat: 'Retention', items: ['Memory capital score', 'Completion streaks', 'Recal depth', 'Feature discovery', 'Cedar vs. user initiated'] },
          { cat: 'Churn', items: ['Decay curve', 'Declined nudges', 'Incomplete onboarding', 'Ticket sentiment', 'Downgrade signals'] },
        ].map((s) => (
          <div key={s.cat}>
            <h3 style={{ fontSize: '11px', fontWeight: 700, color: T.cedarPrimary, marginBottom: '4px' }}>{s.cat}</h3>
            {s.items.map((item) => (
              <div key={item} className="flex items-center gap-1.5" style={{ padding: '2px 0' }}>
                <ChevronRight style={{ width: '10px', height: '10px', color: T.cedarStream, flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: T.textSecondary }}>{item}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ─── Tab 4: Customer Journey — condensed grid ─── */
const CustomerJourneyTab: React.FC = () => {
  const stages = [
    { name: 'Discovery', time: 'Pre-signup', goal: 'Not "get fit" — "get a coach who learns you."', metric: 'Signup conversion', icon: Users },
    { name: 'Onboarding', time: 'Day 0-1', goal: 'User feels "Cedar knows me" by end of Day 1.', metric: 'Completion, time-to-goal', icon: Target },
    { name: 'First Value', time: 'Day 1-3', goal: 'Debrief feels like a real coach, not a template.', metric: 'First session, debrief', icon: Zap },
    { name: 'Habit', time: 'Day 3-14', goal: "Cedar = user's morning fitness check-in.", metric: 'Regularity, check-ins', icon: Heart },
    { name: 'Commitment', time: 'Week 2-4', goal: 'Make leaving feel like losing a coach.', metric: 'Recal engagement, conversion', icon: Shield },
    { name: 'Retention', time: 'Month 1+', goal: 'Every week Cedar gets harder to leave.', metric: 'Monthly retention, memory', icon: Brain },
  ];

  return (
    <div>
      <p style={sectionLabel}>Customer Journey Map</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {stages.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.name} style={card}>
              <div className="flex items-center gap-2 mb-2">
                <div style={iconBox(T.cedarSubtle)}><Icon style={{ width: '14px', height: '14px', color: T.cedarPrimary }} /></div>
                <h3 style={{ ...heading, fontSize: '13px' }}>{s.name}</h3>
                <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '9999px', backgroundColor: T.limestone, color: T.textTertiary, marginLeft: 'auto' }}>{s.time}</span>
              </div>
              <div style={{ padding: '6px 8px', borderRadius: T.radiusSm, backgroundColor: T.cedarSubtle, marginBottom: '6px' }}>
                <p style={{ fontSize: '11px', fontWeight: 600, color: T.cedarPrimary }}>Goal</p>
                <p style={{ fontSize: '11px', color: T.textSecondary }}>{s.goal}</p>
              </div>
              <p style={{ fontSize: '11px', color: T.textTertiary }}>Metric: {s.metric}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Tab 5: Experiments ─── */
const ExperimentsTab: React.FC<{ expanded: string | null; setExpanded: (n: string | null) => void }> = ({ expanded, setExpanded }) => (
  <div className="space-y-3">
    <p style={sectionLabel}>Experiments</p>
    {EXPERIMENTS.map((exp) => {
      const isOpen = expanded === exp.name;
      const s = EXPERIMENT_STATUSES[exp.status];
      const SIcon = s.icon;
      return (
        <div key={exp.name} style={{ ...card, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
          onClick={() => setExpanded(isOpen ? null : exp.name)}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = T.shadowLg; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = T.shadowMd; }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '9999px', color: '#FFF', backgroundColor: T.cedarPrimary }}>#{exp.priority}</span>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: T.cedarInk }}>{exp.name}</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1" style={{ fontSize: '11px', fontWeight: 500, padding: '3px 8px', borderRadius: '9999px', backgroundColor: s.bg, color: s.text }}>
                <SIcon style={{ width: '12px', height: '12px' }} />{exp.status}
              </span>
              <ChevronRight style={{ width: '14px', height: '14px', color: T.textTertiary, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
            </div>
          </div>
          {isOpen && (
            <div className="grid grid-cols-3 gap-3" style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${T.limestone}` }}>
              <div>
                <p style={{ fontSize: '10px', fontWeight: 700, color: T.cedarPrimary, marginBottom: '3px' }}>Hypothesis</p>
                <p style={{ fontSize: '12px', color: T.textSecondary, lineHeight: 1.4 }}>{exp.hypothesis}</p>
              </div>
              <div>
                <p style={{ fontSize: '10px', fontWeight: 700, color: T.cedarStream, marginBottom: '3px' }}>Success Metric</p>
                <p style={{ fontSize: '12px', color: T.textSecondary, lineHeight: 1.4 }}>{exp.successMetric}</p>
              </div>
              <div>
                <p style={{ fontSize: '10px', fontWeight: 700, color: T.textTertiary, marginBottom: '3px' }}>Outcome</p>
                <p style={{ fontSize: '12px', color: T.textTertiary, fontStyle: 'italic' }}>{exp.outcome || 'Not recorded yet.'}</p>
              </div>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

/* ─── Tab 6: Roadmap OS ─── */
const RoadmapOSTab: React.FC = () => (
  <div className="space-y-4">
    <div style={card}>
      <p style={sectionLabel}>The One Filter</p>
      <p style={{ fontSize: '15px', fontWeight: 600, color: T.cedarPrimary }}>Does this fight the 90% abandonment rate?</p>
      <p style={{ fontSize: '12px', color: T.textSecondary, marginTop: '4px', lineHeight: 1.4 }}>Every roadmap item must pass this filter. If it doesn't reduce churn, increase completion, or deepen memory capital — it waits.</p>
    </div>

    <p style={sectionLabel}>Operating Tempo</p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {[
        { period: 'Weekly', color: T.cedarPrimary, bg: T.cedarSubtle, items: ['Review North Star deltas', 'Ship or kill 1 experiment', 'Clear bug backlog', 'Sync Paul on GTM'] },
        { period: 'Bi-weekly', color: T.cedarStream, bg: `${T.cedarStream}15`, items: ['User interview (1 real user)', 'Review cohort retention', 'Roadmap re-rank', 'Kill 1 stale initiative'] },
        { period: 'Monthly', color: T.textSecondary, bg: T.limestone, items: ['Full funnel audit', 'Experiment retrospective', 'Memory capital audit'] },
      ].map((t) => (
        <div key={t.period} style={card}>
          <div className="flex items-center gap-2 mb-2">
            <div style={iconBox(t.bg)}><Calendar style={{ width: '12px', height: '12px', color: t.color }} /></div>
            <h3 style={{ ...heading, fontSize: '13px' }}>{t.period}</h3>
          </div>
          {t.items.map((item) => (
            <div key={item} className="flex items-start gap-1.5" style={{ padding: '2px 0' }}>
              <ChevronRight style={{ width: '10px', height: '10px', marginTop: '3px', flexShrink: 0, color: T.cedarStream }} />
              <span style={{ fontSize: '12px', color: T.textSecondary }}>{item}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);
